import { restService } from "@/helpers/services/restService";
import i18n from "@/i18n";
import router from "@/router";
import tenantModel from "@/state/models/tenantModel";
import subscriptionModel from "@/state/models/subscriptionModel";
import { toastrService } from "../notification/toastrService";
import packageModel from "@/state/models/packageModel";
import apiConsumerModel from "@/state/models/apiConsumerModel";
import apiAccessModel from "@/state/models/apiAccessModel";


const tenantUrl: string = restService.buildURL(
  process.env.VUE_APP_API_TENANT_URL
);

const subscriptionUrl: string = restService.buildURL(
  process.env.VUE_APP_API_SUBSCRIPTION_URL
);

const packagesUrl: string = restService.buildURL(
  process.env.VUE_APP_API_PACKAGES_URL
);

const apiConsumersUrl: string = restService.buildURL(
    process.env.VUE_APP_API_CONSUMERS_URL
);

const apiAccessUrl: string = restService.buildURL(
    process.env.VUE_APP_API_ACCESS_URL
);

const generateApiAccessTokenUrl: string = restService.buildURL(
    process.env.VUE_APP_API_GENERATE_ACCESS_TOKE_URL
);

/**
 * Fetch and return all available tenants
 */
async function fetchTenants() {
  const tenants: tenantModel[] = await restService.get(tenantUrl);

  if (!tenants) {
    return [];
  }

  return tenants;
}

/**
 * Fetch and return a single tenant by the given id
 *
 * @param tenantId
 */
async function fetchTenantById(tenantId: string) {
  const tenant: tenantModel = await restService.get(`${tenantUrl}/${tenantId}`);

  if (!tenant) {
    router.push({
      name: "ct-admin",
      params: {
        toastrMessage: i18n.t("admin.tenant.fetch-error").toString(),
        toastrType: toastrService.ERROR_TYPE
      }
    });
  }
  return tenant;
}

/**
 * Fetch and return a single tenant by the given id
 *
 * @param apiConsumerId
 */
async function fetchApiConsumerById(apiConsumerId: string) {
  const apiConsumer: apiConsumerModel = await restService.get(`${apiConsumersUrl}/${apiConsumerId}`);

  if (!apiConsumer) {
    router.push({
      name: "ct-admin",
      params: {
        toastrMessage: i18n.t("admin.tenant.fetch-error").toString(),
        toastrType: toastrService.ERROR_TYPE
      }
    });
  }
  return apiConsumer;
}


async function addNewTenant(tenant: tenantModel) {
  const isTenantCreated = await restService.post(tenantUrl, tenant);

  if (isTenantCreated !== undefined) {
    router.push({
      name: "ct-admin",
      params: {
        toastrMessage: i18n.t("admin.tenant.create-success").toString(),
        toastrType: toastrService.SUCCESS_TYPE
      }
    });
  }
}


async function updateTenant(tenant: tenantModel) {
  const url = `${tenantUrl}/${tenant.id}`;
  const isTenantUpdated = await restService.put(url, tenant);

  if (isTenantUpdated !== undefined) {
    toastrService.showSuccess(i18n.t("admin.tenant.edit-success"));
  } else {
    toastrService.showSuccess(i18n.t("admin.tenant.edit-error"));
  }
}

async function fetchFeaturePackages() {
  return [
    {id: "1234", name: "Info-Sms", costs: "50"},
    {id: "4567", name: "Info-Sms", costs: "50"},
    {id: "8910", name: "Info-Sms", costs: "50"},
    {id: "1112", name: "Info-Sms", costs: "50"},
  ];
}

async function fetchSubscriptionDetails(tenantId: string) {
  return {
    contractTerms: new Date("2030-12-31T21:50:27.173599Z"),
    monthlyCosts: 500,
    subscription: "basic",
    activeFeaturePackages: [{name: "a", id: "1234"}, {name: "b", id: "8910"}]
  }
}

async function fetchAllSubscriptions() {
  const subscriptions: subscriptionModel = await restService.get(subscriptionUrl);

  if (!subscriptions) {
    return [];
  }

  return subscriptions;
}

/**
 * Fetch and return all available Api Consumers
 */
async function fetchApiConsumers() {
  const apiConsumers: apiConsumerModel[] = await restService.get(apiConsumersUrl);

  if (!apiConsumers) {
    return [];
  }

  return apiConsumers;
}

async function addNewApiConsumer(apiConsumer: apiConsumerModel) {
  const isApiConsumerCreated = await restService.post(apiConsumersUrl, apiConsumer);

  if (isApiConsumerCreated !== undefined) {
    await router.push({
      name: "api-consumer-list",
      params: {
        toastrMessage: i18n.t("admin.tenant.create-success").toString(),
        toastrType: toastrService.SUCCESS_TYPE
      }
    });
  }
}

/**
 * Fetch and return all available Api Accesses
 */
async function fetchApiAccessList(apiConsumerId: string) {
  const apiAccess: apiAccessModel[] = await restService.get(apiAccessUrl, {apiConsumerId});

  if (!apiAccess) {
    return [];
  }

  return apiAccess;
}

/**
 * Fetch and return all available Tokens for an ApiAccess
 */
function fetchApiToken(apiAccessId: string) {
  const apiToken = restService.get(apiAccessUrl + '/' + apiAccessId + '/tokens');

  if (!apiToken) {
    return [];
  }

  return apiToken;
}

async function deleteApiAccess(apiAccessId: string) {
  const response = await restService.del(apiAccessUrl + '/' + apiAccessId);

  if (response !== undefined) {
    toastrService.showSuccess(i18n.t("admin.consumer.token.delete-success").toString())
  } else {
    toastrService.showError(i18n.t("admin.consumer.token.delete-error").toString())
  }
}

async function addNewApiAccessToken(apiAccess: apiAccessModel) {
  // TODO: APiConsumer ID is missing
  const isApiAccessCreated = await restService.post(generateApiAccessTokenUrl, apiAccess);

  if (isApiAccessCreated !== undefined) {
    toastrService.showSuccess(i18n.t("admin.consumer.token.create-success").toString())
  } else {
    toastrService.showError(i18n.t("admin.consumer.token.create-error").toString())
  }
  return true;
}


export const adminService = {
  fetchTenants,
  fetchTenantById,
  addNewTenant,
  updateTenant,
  fetchFeaturePackages,
  fetchSubscriptionDetails,
  fetchAllSubscriptions,
  fetchApiConsumers,
  addNewApiConsumer,
  addNewApiAccessToken,
  fetchApiAccessList,
  fetchApiConsumerById,
  fetchApiToken,
  deleteApiAccess
}